import React from 'react';
import { FileText } from 'lucide-react';
import { Button } from '../../ui/Button';

interface NotesSettingsProps {
  generalSettings: {
    notesEnabled: boolean;
  };
  isNotesSettingsSaving: boolean;
  onNotesChange: (enabled: boolean) => void;
  onSave: () => void;
  onReset: () => void;
}

export const NotesSettings: React.FC<NotesSettingsProps> = ({
  generalSettings,
  isNotesSettingsSaving,
  onNotesChange,
  onSave,
  onReset
}) => {
  return (
    <div>
      <div className="mb-6">
        <h4 className="text-lg font-medium flex items-center" style={{ color: 'var(--color-text-primary)' }}>
          <FileText className="w-5 h-5 mr-2" style={{ color: 'var(--color-accent)' }} />
          Notes System
        </h4>
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Configure the notes feature for providers and appointments
        </p>
      </div>
      
      <div className="max-w-2xl space-y-6">
        <div className="flex items-center justify-between p-4 border rounded-lg" style={{ 
          backgroundColor: 'var(--color-bg-secondary)',
          borderColor: 'var(--color-border)'
        }}>
          <div>
            <h5 className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
              Enable Notes Feature
            </h5>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Allow providers and staff to add notes to appointments and patient records
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
                          <input
                type="checkbox"
                checked={generalSettings.notesEnabled}
                onChange={(e) => onNotesChange(e.target.checked)}
                className="sr-only peer"
              />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        
        {generalSettings.notesEnabled && (
          <div className="space-y-4 p-4 border rounded-lg" style={{ 
            backgroundColor: 'var(--color-bg-tertiary)',
            borderColor: 'var(--color-border)'
          }}>
            <h6 className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
              Notes Configuration
            </h6>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Rich text editing enabled</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>File attachments supported</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Note history tracking</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Provider collaboration</span>
              </div>
            </div>
          </div>
        )}
        
        {!generalSettings.notesEnabled && (
          <div className="p-4 border rounded-lg" style={{ 
            backgroundColor: 'var(--color-bg-secondary)',
            borderColor: 'var(--color-warning)',
            borderStyle: 'dashed'
          }}>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              The notes feature is currently disabled. Enable it above to allow providers and staff to add notes to appointments and patient records.
            </p>
          </div>
        )}
        
        {/* Save and Reset Buttons */}
        <div className="flex justify-end space-x-3 mt-8">
          <Button
            variant="secondary"
            onClick={onReset}
            size="sm"
          >
            Reset to Defaults
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={isNotesSettingsSaving}
            size="sm"
          >
            {isNotesSettingsSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};
